import { Divider, Form, Input, Select, Slider, Switch } from "antd"
import { useRef } from "react"
import debounce from "lodash/debounce"
import {
  HardwareDevices,
  IDraftShiftDataDocsEntity,
  IShiftPlanDoc,
} from "../typings"
import {
  IndentedSection,
  SettingRow,
} from "../setup/components/CreateShiftPlanConfiguration"
import SettingModification from "./SettingModification"
import { FaceSmileIcon } from "@heroicons/react/24/outline"
import {
  DateInputField,
  toDate,
  toIsoDateString,
} from "../setup/components/CreateShiftPlanDetails"
import { DateInputValue } from "../setup/components/DateInput"
import {
  parseApiDate,
  isBeforeToday,
  isSameDate,
  isCompleteDate,
} from "../utils/shiftTablesUtils"

// ─── Constants ────────────────────────────────────────────────────────────────
const HARDWARE_DEVICE_OPTIONS = [
  { label: "Fingerprint Device", value: "fingerprint" },
  { label: "Facial recognition", value: "facial_recognition" },
  { label: "Palm recognition", value: "palm_recognition" },
  { label: "RFID Cards", value: "rfid_card" },
  { label: "Device not listed here", value: "unknown" },
]

const BUFFER_WINDOW_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
]
export const NOTIFICATION_CADENCE_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "240", label: "2 hours" },
]

export const sufficeIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m19.5 8.25-7.5 7.5-7.5-7.5"
    />
  </svg>
)

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  shiftPlan: IShiftPlanDoc | IDraftShiftDataDocsEntity
  onUpdate: (id: number, body: Record<string, any>) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

const NamePermissionAndClockIn = ({ shiftPlan, onUpdate }: Props) => {
  const lastPayloadRef = useRef<string>("")

  // ── Debounced save — created once, stable across renders ──────────────────
  // .cancel() is called automatically when the component unmounts because
  // React will GC the ref; no useEffect cleanup needed.
  const debouncedSave = useRef(
    debounce((values: any, plan: IShiftPlanDoc | IDraftShiftDataDocsEntity) => {
      if (!plan?.id) return

      // Don't fire the request while the user is mid-edit on a date field
      // (e.g. a digit cleared) — wait until both dates are complete & valid.
      if (
        !isCompleteDate(values.startDate) ||
        !isCompleteDate(values.endDate) ||
        !toIsoDateString(values.startDate) ||
        !toIsoDateString(values.endDate)
      )
        return

      // Don't submit a start/end date the user just moved into the past.
      const startMovedToPast =
        !isSameDate(values.startDate, parseApiDate(plan.startDate).value) &&
        isBeforeToday(values.startDate)
      const endMovedToPast =
        !isSameDate(values.endDate, parseApiDate(plan.endDate).value) &&
        isBeforeToday(values.endDate)
      if (startMovedToPast || endMovedToPast) return

      const body = {
        title: values.title,
        type: plan.type,
        allowManualTimeEntry: values.allowManualTimeEntry,
        requireApprovalForManualTimeEntry:
          values.requireApprovalForManualTimeEntry,
        allowedClockInMinutesBeforeShift: plan.allowedClockInMinutesBeforeShift,
        allowOvertime: values.allowOvertime,
        linkHardwareDevice: values.linkHardwareDevice,
        hardwareDevices: values.hardwareDevices ?? [],
        ...(values.hardwareSetupContactEmail
          ? { hardwareSetupContactEmail: values.hardwareSetupContactEmail }
          : {}),
        allowGeoFencing: values.allowGeoFencing,
        geofencingOffsetMeters: Number(values.geofencingOffsetMeters ?? 0),
        allowSwapping: values.allowSwapping,
        useOvertimeRecord: values.useOvertimeRecord,
        notificationCadence: Array.isArray(values.notificationCadence)
          ? String(values.notificationCadence[0] ?? "")
          : String(values.notificationCadence ?? ""),
        notifiyMeBefore: values.notifiyMeBefore || false,
        smsNotification: values.smsNotification,
        requireApprovalForSwap: values.requireApprovalForSwap,
        allowLateClockIn: values.allowLateClockIn,
        useInPayrollCalculation: values.useInPayrollCalculation,
        addOvertimeToPayroll:
          values.addOvertimeToPayroll ?? plan.addOvertimeToPayroll,
        allowedLateClockinInMinutes: Number(
          values.allowedLateClockinInMinutes ??
            plan.allowedLateClockinInMinutes ??
            0
        ),
        status: plan.status,
        startDate: toIsoDateString(values.startDate),
        endDate: toIsoDateString(values.endDate),
      }

      // Skip if payload hasn't changed (same as before)
      const signature = JSON.stringify(body)
      if (signature === lastPayloadRef.current) return
      lastPayloadRef.current = signature

      onUpdate(plan.id, body)
    }, 600)
  ).current
  const [form] = Form.useForm()

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(_, values) => debouncedSave(values, shiftPlan)}
      initialValues={{
        title: shiftPlan.title || "",
        allowedLateClockinInMinutes:
          shiftPlan?.allowedLateClockinInMinutes != null
            ? String(shiftPlan.allowedLateClockinInMinutes)
            : "0",
        hardwareDeviceTypes: shiftPlan.hardwareDeviceTypes
          ? shiftPlan.hardwareDeviceTypes.map((d: string | HardwareDevices) =>
              typeof d === "string" ? d : d.deviceType
            )
          : [],
        allowManualTimeEntry: shiftPlan.allowManualTimeEntry || false,
        requireApprovalForManualTimeEntry:
          !!shiftPlan.requireApprovalForManualTimeEntry || false,
        allowSwapping: !!shiftPlan.allowSwapping || false,
        useOvertimeRecord: !!shiftPlan.useOvertimeRecord || false,

        notificationCadence: shiftPlan.notificationCadence || "",
        notifiyMeBefore: !!shiftPlan.notifiyMeBefore || false,
        smsNotification: !!shiftPlan.smsNotification || false,
        linkHardwareDevice: !!shiftPlan.linkHardwareDevice || false,
        requireApprovalForSwap: !!shiftPlan.requireApprovalForSwap || false,
        allowLateClockIn: !!shiftPlan.allowLateClockIn || false,
        useInPayrollCalculation: !!shiftPlan.useInPayrollCalculation || false,
        addOvertimeToPayroll: !!shiftPlan.addOvertimeToPayroll || false,
        allowOvertime: !!shiftPlan.allowOvertime || false,
        hardwareSetupContactEmail: shiftPlan.hardwareSetupContactEmail || "",
        allowGeoFencing: !!shiftPlan.allowGeoFencing || false,
        geofencingOffsetMeters: shiftPlan.geofencingOffsetMeters ?? 0,
        startDate: parseApiDate(shiftPlan.startDate).value,
        endDate: parseApiDate(shiftPlan.endDate).value,
      }}
    >
      <div className="flex flex-col gap-5">
        {/* ── Name ── */}
        <div className="border border-gray-300 rounded-t-lg overflow-hidden">
          <SectionHeader title="Name" />
          <span className="flex flex-col gap-2 px-4 py-3">
            <p className="font-bold">Shift plan name</p>
            <span className="border border-gray-300 rounded-md w-full p-2">
              <Form.Item
                name="title"
                style={{ margin: 0 }}
              >
                <Input
                  bordered={false}
                  style={{ width: "100%", padding: 0 }}
                />
              </Form.Item>
            </span>
          </span>
          <Form.Item className="!mb-0 px-4 py-3">
            <div className="flex gap-4 items-end">
              <div>
                <p className="font-bold mb-1">Start Date:</p>
                <Form.Item
                  name="startDate"
                  noStyle
                  rules={[
                    {
                      validator: (_, value: DateInputValue) => {
                        if (!value?.dd || !value?.mm || !value?.yyyy)
                          return Promise.reject("Start date is required")
                        const d = toDate(value)
                        if (d && isNaN(d.getTime()))
                          return Promise.reject("Invalid start date")
                        // only block a past date if the user actually changed it
                        // from what's already saved on the plan
                        const savedStart = parseApiDate(
                          shiftPlan.startDate
                        ).value
                        if (
                          !isSameDate(value, savedStart) &&
                          isBeforeToday(value)
                        )
                          return Promise.reject(
                            "Start date cannot be in the past"
                          )
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <DateInputField />
                </Form.Item>
              </div>

              <span className="text-[#94A3B8] pb-2.5">—</span>

              <div>
                <p className="font-bold mb-1">End Date:</p>
                <Form.Item
                  name="endDate"
                  noStyle
                  rules={[
                    {
                      validator: (_, value: DateInputValue) => {
                        if (!value?.dd || !value?.mm || !value?.yyyy)
                          return Promise.reject("End date is required")
                        const end = toDate(value)
                        if (end && isNaN(end.getTime()))
                          return Promise.reject("Invalid end date")
                        // only block a past date if the user actually changed it
                        // from what's already saved on the plan
                        const savedEnd = parseApiDate(shiftPlan.endDate).value
                        if (
                          !isSameDate(value, savedEnd) &&
                          isBeforeToday(value)
                        )
                          return Promise.reject(
                            "End date cannot be in the past"
                          )
                        const start = toDate(
                          form.getFieldValue("startDate") ?? {}
                        )
                        if (start && end && end < start)
                          return Promise.reject(
                            "End date cannot be before start date"
                          )
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <DateInputField />
                </Form.Item>
              </div>
            </div>
          </Form.Item>
        </div>

        {/* ── Permissions & Clock-in ── */}
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <SectionHeader title="Permission & Clock-in methods" />
          <SettingModification
            BUFFER_WINDOW_OPTIONS={BUFFER_WINDOW_OPTIONS}
            NOTIFICATION_CADENCE_OPTIONS={NOTIFICATION_CADENCE_OPTIONS}
            HARDWARE_DEVICE_OPTIONS={HARDWARE_DEVICE_OPTIONS}
            sufficeIcon={sufficeIcon}
            form={form}
          />
        </div>
      </div>
    </Form>
  )
}

export default NamePermissionAndClockIn

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader = ({ title }: { title: string }) => (
  <ul className="items-center bg-slate-50 border-b border-gray-300 text-slate-500 py-3 px-4 font-semibold">
    <li>{title}</li>
  </ul>
)

export const RowDivider = () => (
  <Divider
    className="bg-slate-100"
    style={{ margin: 0, padding: 0 }}
  />
)

const SwitchRow = ({
  name,
  title,
  description,
}: {
  name: string
  title: string
  description: string
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-3">
      <p className="font-semibold">{title}</p>
      <Form.Item
        name={name}
        valuePropName="checked"
        style={{ margin: 0, padding: 0 }}
      >
        <Switch className="bg-[#ededed]" />
      </Form.Item>
    </div>
    <p className="text-sm">{description}</p>
  </div>
)
